/**
 * Shared skill types — single source of truth for the API response shape.
 * Used by LibraryPage, LibrarySidebar, FullLibrary, LibraryBottomSheet, and SkillEditor.
 */

import { z } from 'zod';
import { LibraryVisibilitySchema, SkillEntrySchema } from '@shared/schemas/library-entity';

/**
 * Zod schema matching the SkillBase API response shape.
 * Used for trust-boundary validation of POST/PUT /api/v4/skills responses.
 */
export const SkillBaseSchema = z.object({
  id: z.string(),
  slug: z.string(),
  name: z.string(),
  description: z.string(),
  icon: z.string().nullable(),
  content: z.string(),
  visibility: z.enum(['personal', 'team']),
  createdById: z.number(),
  forkedFromId: z.string().nullable(),
  usageCount: z.number(),
  requiresTools: z.array(z.string()),
  availableIn: z.array(z.string()),
});

/** Base skill fields returned by all API endpoints. */
export interface SkillBase {
  id: string;
  /** Canonical, immutable URL-safe identifier (e.g. "meeting-prep-brief").
   *  Used as the argument to the agent's `Skill` tool and as the leading
   *  `/<slug>` token in the composer. Generated server-side on create from
   *  `name`. */
  slug: string;
  name: string;
  description: string;
  icon: string | null;
  content: string;
  visibility: 'personal' | 'team';
  createdById: number;
  forkedFromId: string | null;
  usageCount: number;
  /** Tool slugs the skill needs the agent to be able to call. The agent loop
   *  unions this onto the surface's base tools when the skill is pre-invoked.
   *  Empty array = no extra tools beyond the surface's base set. */
  requiresTools: string[];
  /** Library-applicability tags (U2) — which product surfaces this skill
   *  appears on in the library/picker. Values come from `KNOWN_SURFACES_CLIENT`
   *  (mirror of the server's `KNOWN_SURFACES`). Empty array = hidden
   *  everywhere. Defaults to `['edit-with-ai']` server-side on create. */
  availableIn: string[];
}

/**
 * Skill with computed fields from the list endpoint (GET /api/v4/skills): the
 * server's list-entry schema, with `visibility` closed to the two members the
 * client branches on and the dates read as the ISO strings on the wire.
 */
export const SkillListItemSchema = SkillEntrySchema.omit({ enabled: true }).extend({
  visibility: LibraryVisibilitySchema,
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
});
export type SkillListItem = z.output<typeof SkillListItemSchema>;
